/**
 * Export-Helper für PDF und Excel.
 * Wird per Lazy-Import in den Pages eingebunden, damit die jsPDF/xlsx-Libs
 * nicht im Haupt-Bundle landen.
 */

export interface ExportColumn {
  header: string;
  /** Welcher Key im Row-Objekt enthält den Wert. */
  key: string;
  /** Wenn gesetzt, wird der Wert für den Export formatiert. */
  format?: (value: unknown) => string;
  /** Excel-Spaltenbreite (Default: 18). */
  width?: number;
  /** Wenn true → Werte als Zahl (nicht String) in Excel exportieren. */
  numeric?: boolean;
}

export async function exportToExcel<T extends Record<string, unknown>>(
  rows: T[],
  columns: ExportColumn[],
  filename: string,
  sheetName = 'Daten',
): Promise<void> {
  const XLSX = await import('xlsx');
  const data = rows.map((r) => {
    const obj: Record<string, unknown> = {};
    for (const c of columns) {
      const raw = r[c.key];
      if (c.numeric) {
        obj[c.header] = typeof raw === 'number' ? raw : Number(raw);
      } else if (c.format) {
        obj[c.header] = c.format(raw);
      } else {
        obj[c.header] = raw;
      }
    }
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  ws['!cols'] = columns.map((c) => ({ wch: c.width ?? 18 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31));
  XLSX.writeFile(wb, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}

export async function exportToPDF<T extends Record<string, unknown>>(
  rows: T[],
  columns: ExportColumn[],
  filename: string,
  meta: { title: string; subtitle?: string },
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  await import('jspdf-autotable');
  // jspdf-autotable hängt sich als Methode an die jsPDF-Instanz
  // (TS-Types sind etwas zickig, daher cast)
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  // Header
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(meta.title, 14, 16);
  if (meta.subtitle) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(meta.subtitle, 14, 22);
    doc.setTextColor(0);
  }

  // Tabelle
  const body = rows.map((r) =>
    columns.map((c) => {
      const raw = r[c.key];
      if (c.format) return c.format(raw);
      return raw === null || raw === undefined ? '' : String(raw);
    }),
  );

  // @ts-expect-error autoTable wird zur Laufzeit attached
  doc.autoTable({
    head: [columns.map((c) => c.header)],
    body,
    startY: meta.subtitle ? 26 : 20,
    styles: { fontSize: 8, cellPadding: 1.5, font: 'helvetica' },
    headStyles: { fillColor: [14, 123, 209], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: Object.fromEntries(
      columns.map((c, i) => [
        i,
        { halign: c.numeric ? 'right' : 'left', cellWidth: c.width ?? 'auto' },
      ]),
    ),
  });

  // Footer mit Datum
  const pageCount = (doc as unknown as { internal: { getNumberOfPages: () => number } })
    .internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    const date = new Date().toLocaleDateString('de-DE');
    doc.text(
      `Erstellt: ${date} · Seite ${i}/${pageCount}`,
      doc.internal.pageSize.getWidth() - 14,
      doc.internal.pageSize.getHeight() - 8,
      { align: 'right' },
    );
  }

  doc.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
}

export async function exportToCSV<T extends Record<string, unknown>>(
  rows: T[],
  columns: ExportColumn[],
  filename: string,
): Promise<void> {
  const head = columns.map((c) => csvEscape(c.header)).join(';');
  const lines = [head];
  for (const r of rows) {
    const line = columns
      .map((c) => {
        const raw = r[c.key];
        if (c.format) return csvEscape(c.format(raw));
        return csvEscape(raw === null || raw === undefined ? '' : String(raw));
      })
      .join(';');
    lines.push(line);
  }
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function csvEscape(s: string): string {
  if (s == null) return '';
  if (s.includes(';') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
